import { useEffect, useMemo, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Toaster } from "sonner";
import { Nav } from "./components/Nav";
import { Hero } from "./components/Hero";
import { Manifesto, PersonalLetter } from "./components/SectionsA";
import { SectionParticles } from "./components/SectionParticles";
import { AudienceFit, Roadmap, Curriculum } from "./components/SectionsB";
import { HostSection, Transformation, Takeaways } from "./components/SectionsC";
import { FaqSection, FinalRegister, Footer, MobileStickyCta } from "./components/SectionsD";
import { PrivacyPolicy, TermsPage } from "./pages/LegalPage";
import BookingPage from "./pages/BookingPage";
import ThankYouPage from "./pages/ThankYouPage";

gsap.registerPlugin(ScrollTrigger);

const CustomCursor = () => {
  const dotRef = useRef(null);
  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const dot = dotRef.current;
    dot.style.display = "block";
    const move = (e) => {
      gsap.to(dot, { x: e.clientX, y: e.clientY, duration: 0.18, ease: "power2.out" });
      const cta = e.target.closest("[data-cta], button, a, select, input");
      gsap.to(dot, { scale: cta ? 2.33 : 1, duration: 0.25 });
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return <div ref={dotRef} className="cursor-dot" style={{ display: "none" }} />;
};

const Landing = () => {
  const attribution = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const pick = (k) => params.get(k) || undefined;
    return {
      utm_source: pick("utm_source"),
      utm_medium: pick("utm_medium"),
      utm_campaign: pick("utm_campaign"),
      utm_content: pick("utm_content"),
      utm_term: pick("utm_term"),
      fbclid: pick("fbclid"),
      gclid: pick("gclid"),
      page_url: window.location.href,
      referrer: document.referrer || undefined,
      timestamp: new Date().toISOString(),
    };
  }, []);

  useEffect(() => {
    const tweens = [];

    gsap.utils.toArray("[data-parallax]").forEach((el) => {
      const depth = parseFloat(el.dataset.parallax) || 0.3;
      tweens.push(
        gsap.fromTo(
          el,
          { y: depth * 140 },
          {
            y: -depth * 140,
            ease: "none",
            scrollTrigger: { trigger: el.parentElement, start: "top bottom", end: "bottom top", scrub: true },
          }
        )
      );
    });

    const mainEl = document.querySelector("main.grid-bg");
    const gridST = ScrollTrigger.create({
      start: 0,
      end: "max",
      onUpdate: (self) => {
        if (mainEl) mainEl.style.backgroundPosition = `0px ${-(self.scroll() * 0.3)}px`;
      },
    });

    return () => {
      tweens.forEach((t) => { t.scrollTrigger?.kill(); t.kill(); });
      gridST.kill();
    };
  }, []);

  return (
    <>
      <Nav />
      <SectionParticles />
      <main className="relative z-[1] grid-bg">
        <Hero attribution={attribution} />
        <Manifesto />
        <PersonalLetter />
        <AudienceFit />
        <Roadmap />
        <Curriculum />
        <HostSection />
        <Transformation />
        <Takeaways />
        <FaqSection />
        <FinalRegister attribution={attribution} />
      </main>
      <Footer />
      <MobileStickyCta />
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="bg-[#0E2A54] text-[#D9E4F5] min-h-screen antialiased selection:bg-[#FFFFFF] selection:text-[#0E2A54]">
      <div className="grain-overlay" aria-hidden="true" />
      <CustomCursor />
      <Toaster theme="dark" position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
